<template>
  <div
    class="template-card"
    :class="templateLocation"
    @click="handleChooseTemplate"
    @mouseover="handleHoverTemplate"
  >
    <div class="staff-choice" v-if="isShowBadge(presentation)">
      <img src="@/assets/images/community/staff-choice.svg">
    </div>
    <div v-if="$slots.thumbnail">
      <slot name="thumbnail" />
    </div>
    <div
      :class="[
        'template-thumbnail',
        {
          'has-thumbnail': hasThumbnail,
        }
      ]"
      v-else
    >
      <!-- use data-src for lazy-loading. Check initLazyLoad() -->
      <div
        v-if="hasThumbnail"
        class="thumbnail-wrapper"
      >
        <!-- use v-show here, not v-if for <img> or lazyload will break -->
        <img
          class="thumbnail-image-with-skeleton"
          @load="onThumbnailLoad"
          :src="getThumbnailUrl"
          alt="thumbnail"
        >
      </div>
      <img
        class="object-cover w-full h-full thumbnail-image"
        v-else
        :src="defaultThumbnail"
        alt="Thumbnail default"
      />
    </div>
    <div
      class="template-thumbnail__hover"
      :class="!hasDescription ? 'justify-center items-center' : ''"
    >
      <span>{{ getPresentationDescription }}</span>
    </div>
    <div class="flex w-full p-2 pb-4 template-name-container">
      <img
        v-if="isOfficial"
        class="mr-2 logo-user-template"
        :src="require('@/assets/images/icon/aha-official-avt.svg')"
      >
      <aha-avatar
        v-else
        class="mt-1 mr-2 logo-user-template"
        :name="getAuthorName"
        :image="getAuthorAvt"
        :width="28"
        :height="28"
      />
      <div class="flex-wrap inline-flex w-full template-container-info">
        <b class="template-name w-full" :title="presentation.name">
          {{ presentation.name }}
        </b>
        <span class="template-author w-full">
          <img
            v-if="isOfficial"
            class="inline mr-0.5"
            :src="require('@/assets/images/icon/author-checked.svg')"
          >
          <span class="mt-0.5 truncate">
            {{ isOfficial ? 'AhaSlides Official' : getAuthorName }}
          </span>
        </span>
      </div>
    </div>
    <div class="template-bottom">
      <span class="information flex text-xs">
        <p class="inline-flex items-center mr-3 2xl:mr-4">
          <span class="icon-container">
            <aha-icon
              class="icon-slides"
              name="slides"
              width="10"
              height="10"
              viewBox="0 0 12 12"
              v-tooltip="tooltipNumOfSlides"
            />
          </span>
          {{ presentation.slideCount }}
        </p>
        <p class="inline-flex items-center mr-3 2xl:mr-4 container-item-bottom">
          <span class="icon-container">
            <aha-icon
              class="icon-download-2 text-secondary-50"
              name="download-2"
              viewBox="0 0 14 16"
              width="9"
              v-tooltip="tooltipNumOfDownloads"
            />
          </span>
          {{ timesCopied | formatNumber }}
        </p>
      </span>
      <div class="get">
        <aha-antd-button
          v-aha-emit-action
          name="get_template_btn"
          :class="{'hovering': isCopySlides}"
          @click.stop="copySlides(presentation)"
          :submitting="isCopySlides"
          is-loading-inline
          :disabled="disableGetTemplate"
          v-if="isCopyable"
        >
          {{ $i18nPresenter.t("Get") }}
        </aha-antd-button>

        <aha-antd-button
          v-else
          v-aha-emit-action
          name="get_template_btn"
          :class="{'hovering': !!copyingTemplateId && copyingTemplateId === presentation.id}"
          @click.stop="getTemplate(presentation)"
          :submitting="!!copyingTemplateId && copyingTemplateId === presentation.id"
          is-loading-inline
          :disabled="disableGetTemplate"
        >
          {{ $i18nPresenter.t("Get") }}
        </aha-antd-button>
      </div>
    </div>
    <TemplatePreviewPopup
      v-if="isShowingTemplatePreviewPopup"
      @hide="toggleTemplatePreviewPopup"
      :presentationShareCode="presentation.shareCode"
      :templateLocation="templateLocation"
      :where="where"
      :isCopyable="isCopyable"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import Utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import TemplatePreviewPopup from '@/components-v2/template/TemplatePreviewPopup.vue'
import { ROUTE_NAMES } from '@/constant/routes'

const defaultThumbnail = require('@/assets/images/icon/default-thumbnail.svg')

export default {
  components: {
    TemplatePreviewPopup,
  },
  props: {
    size: {
      type: String,
      default: 'md',
    },
    presentation: {
      type: Object,
      required: true,
    },
    customHandler: {
      type: Function,
      required: false,
      default: null,
    },
    templateLocation: {
      type: String,
      default: null,
    },
    where: {
      type: String,
      default: '',
    },
    isCopyable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      defaultThumbnail,
      isThumbnailLoaded: false,
      isShowingTemplatePreviewPopup: false,
      isCopySlides: false,
    }
  },
  computed: {
    ...mapGetters('presentationTemplates', [
      'copyingTemplateId',
    ]),
    ...mapGetters('featureToggles', [
      'canUseNewComunityTemplate',
    ]),
    ...mapState({
      currentUser: (state) => state.currentUser,
      canEditPresentation: (state) => state.canEditPresentation,
    }),
    tooltipNumOfSlides() {
      return this.$i18nPresenter.t('Number of slides')
    },
    tooltipNumOfDownloads() {
      return this.$i18nPresenter.t('Number of downloads')
    },
    hasThumbnail() {
      return this.presentation.thumbnailImage || this.presentation.customThumbnailImage
    },
    isOfficial() {
      return this.presentation.User && this.presentation.User.type === 'official'
    },
    numOfSlides() {
      return this.presentation.slideCount || '0'
    },
    timesCopied() {
      return this.presentation.copyCount || '0'
    },
    getAuthorName() {
      if (this.presentation.User) {
        return `${this.presentation.User.firstName} ${this.presentation.User.lastName}`
      }
      return ''
    },
    getAuthorAvt() {
      if (this.presentation.User) {
        return this.presentation.User.avatar
      }
      return ''
    },
    getThumbnailUrl() {
      if (this.presentation.customThumbnailImage) {
        return this.presentation.customThumbnailImage
      }
      return this.presentation.thumbnailImage
    },
    getPresentationDescription() {
      if (this.presentation.description) {
        return this.presentation.description
      }
      return 'Click to see details.'
    },
    hasDescription() {
      if (this.presentation.description && this.presentation.description.length > 0) {
        return true
      }
      return false
    },
    disableGetTemplate() {
      return this.$route.name === ROUTE_NAMES.PRESENTATION_DETAIL && !this.canEditPresentation
    },
  },
  methods: {
    ...mapActions('presentationTemplates', ['copyFromTemplateAction', 'copySlidesAction']),
    toggleTemplatePreviewPopup() {
      this.isShowingTemplatePreviewPopup = !this.isShowingTemplatePreviewPopup
      if (this.isShowingTemplatePreviewPopup) {
        document.body.classList.add('no-scroll-template')
        Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'Preview template', this.where, this.presentation.id)
      } else {
        document.body.classList.remove('no-scroll-template')
      }
    },
    onThumbnailLoad() {
      this.isThumbnailLoaded = true
    },
    handleCopyTemplateSuccess(presentation) {
      const newPresentationId = presentation.id
      const params = { id: newPresentationId }
      this.$router.push({ name: 'PresentationDetail', params })
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'Create new presentation', 'Use template', null, {
        sourcePresentationId: this.presentation.id,
        presentationId: newPresentationId,
      })
    },
    handleChooseTemplate() {
      if (this.customHandler) {
        this.customHandler(this.presentation)
        return
      }
      this.toggleTemplatePreviewPopup()
    },
    getTemplate(templateData) {
      Utils.mixpanelTracking('Template Library > Listing', 'Get template', this.where, templateData.id)
      this.copyFromTemplateAction({
        template: templateData,
        callback: this.handleCopyTemplateSuccess,
      })
    },
    handleHoverTemplate() {
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'Templates > Hover', this.where, this.presentation.id)
    },
    goToLiveDemo() {
      this.$router.push({ name: 'PresentationPlayDemo', query: { id: this.presentation.id } })
      Utils.mixpanelTracking(eventCategories.TEMPLATE_LIBRARY, 'User clicks demo on template', this.presentation.id)
    },
    async copySlides(presentation) {
      if (this.isCopySlides) return
      this.isCopySlides = true
      await this.copySlidesAction(presentation.id)
      this.isCopySlides = false
    },
    isShowBadge(presentation) {
      return presentation.isAdminPick && this.canUseNewComunityTemplate
    },
  },
}
</script>

<style lang="scss" scoped>
  .template-card {
    width: 100%;

    .get {
      @apply ml-auto;
    }
    &:hover {
      box-shadow: 0 20px 20px -20px rgba(0,0,0,.2);
      transition: .5s;
      transition-timing-function: cubic-bezier(.33,1,.68,1);
    }
    @apply rounded-lg shadow-card cursor-pointer;
    @apply hover:text-primary-90 transition-colors;
    @apply relative inline-block;
    background: white;
    &:hover {
      .template-thumbnail {
        @apply border-primary-90;
      }
    }
    .template-author {
      @apply flex items-center;
    }
    .spinner {
      @apply w-auto;
    }

  }
  .template-thumbnail {
    @apply cursor-pointer relative mb-2 flex items-center justify-center rounded-xl;
    .thumbnail-wrapper {
      position: relative;
      width: 100%;
      padding-top: 56.25%; /* 16:9 Aspect Ratio */

      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px 8px 0 0;
      }
    }

    .thumbnail-image {
      border-top-left-radius: 0.5rem;
      border-top-right-radius: 0.5rem;
    }
    .thumbnail-image-with-skeleton {
      border-top-left-radius: 0.5rem;
      border-top-right-radius: 0.5rem;
      @apply w-full h-full;
    }
  }

  .template-thumbnail__hover {
    @apply absolute top-0 left-0 w-full h-full p-4 flex opacity-0;
    @apply transition-opacity border-none;
    @apply text-base-80;
    border-width: 2px;
    background: white;
    border-radius: 7px 7px 8px 8px;
    // hover-out
    transition: 1s;
    transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
    box-shadow: none;
    &:hover { // hover-in
      box-shadow: 0px 20px 20px -20px rgba(0, 0, 0, .2);
      transition: 0.5s;
      transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
    }
    span {
      white-space: pre-wrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .template-container-info {
    max-width: calc(100% - 36px); // 36px = avatar width + margin
  }

  .template-name {
    word-break: break-word;
    overflow: hidden;
    line-height: 1.125rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .template-statistic {
    border-radius: 1rem;
    bottom: 0.5rem;
    right: 0.5rem;
    background: #3F4557;
    color: white;
    opacity: 0.6;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
  .object-cover {
    border-radius: 8px 8px 0px 0px;
  }

  .template-name-container {
    display: flex;
    align-items: center;
    padding-left: 16px;
    padding-right: 16px;
    .logo-user-template{
      width: 40px;
      height: 40px;
    }
    .template-name{
      font-style: normal;
      font-weight: 700;
      font-size: 16px;
      line-height: 22px;
      color: #282E3E;
    }
    .template-author{
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 18px;
      color: #707070;
    }
  }
  .template-desc {
    margin: 0 16px 16px;
    height: fit-content;
    min-height: 36px;

    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;

    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: #707070;
  }
  .template-bottom {
    display:flex;
    margin: 0 16px 16px 16px;
    .information {
      color: rgba(49, 49, 49, 1);
      margin-top: 4px;

      .container-item-bottom {
        margin-right: 8px;
      }
      .icon-container {
        position: relative;
        width: 16px;
        svg {
          width: 12px;
          height: 12px;
          display: inline-flex;
          vertical-align: baseline;
          color: rgba(49, 49, 49, 1);
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .template-card {
      .template-name-container {
        .logo-user-template {
          width: 36px;
          height: 36px;
        }
      }
    }
  }
  .staff-choice {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    img {
      max-width: 85%;
    }
  }
  // left to -1 because safari has some extra space with left = 0, doesn't affect android because of overflow hidden
  @media (max-width: 960px) {
    .staff-choice {
      left: -1px;
      overflow: hidden;
    }
  }
</style>
